import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { RiskAssessmentSurveyDefinitionsModalComponent } from './modals/risk-assessment-survey-definitions-modal/risk-assessment-survey-definitions-modal.component';
import { AgilityRiskService } from '../../../providers/agility-risk.service';
import { TranslateService } from '@ngx-translate/core';
import { RiskAssessmentSurveyDetailsModalComponent } from './modals/risk-assessment-survey-details-modal/risk-assessment-survey-details-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Assessment } from '../../../models/assessment';
import { AssessmentDistributionRisk } from '../../../models/assessment-distribution-risk';
import { QuestionType } from '../../../models/question-type';
import { AssessmentMemberData } from '../../../models/assessment-member-data';
import { RiskAssessmentMemberAnswersModalComponent } from './modals/risk-assessment-member-answers-modal/risk-assessment-member-answers-modal.component';
import { AssessmentMemberAnswer } from '../../../models/assessment-member-answer';
import { AssessmentDistribution } from '../../../models/assessment-distribution';
import { AssessmentComment } from '../../../models/assessment-comment';
import * as _ from 'lodash';
import { AssessmentDistributionMemberData } from '../../../models/assessment-distribution-member-data';
import { CustomField, CustomFieldConstants } from '../../../models/custom-field.interface';
import { RiskAssessmentSurveyCustomFieldsModalComponent } from './modals/risk-assessment-survey-custom-fields-modal/risk-assessment-survey-custom-fields-modal.component';
import { CustomValue } from '../../../models/custom-value';
import { BaseEntity } from '../../../models/base-entity';
import { AssessmentFactorSelection } from '../../../models/assessment-factor-selection';
import { AgilityRiskFormulaHelperService } from '../../../providers/agility-risk-formula-helper.service';
import { RiskFactorRank } from '../../../models/risk-factor-rank';
import { AgilityRiskMessageService } from '../../../providers/agility-risk-message.service';
import { RiskFactor } from '../../../models/risk-factor';
import { AgilityRiskNavigationService } from '../../../providers/agility-risk-navigation.service';

@Component({
  selector: 'lib-risk-assessment-survey',
  templateUrl: './risk-assessment-survey.component.html',
  styleUrls: ['./risk-assessment-survey.component.scss']
})
export class RiskAssessmentSurveyComponent implements OnInit {
  public overallRiskOptions = [];
  public activeThreatIndexes: any;
  public assessment: Assessment = new Assessment();
  public assessmentDistributionRisks: Array<AssessmentDistributionRisk>;
  public questionType = QuestionType;
  public currentUserAssessmentMemberData: AssessmentMemberData;
  public showAllRisks = true;
  public expandAllRisks: boolean;
  public surveyUserId: string;
  public assessmentDistribution: AssessmentDistribution;
  public distributionId: string;
  public comment: string; // note: extracted from array of comments from assessment object. display the current user's comment.
  public customFields: Array<CustomField> = [];
  public saving: boolean;
  public riskFormulaString: string;
  public customFieldConstants = CustomFieldConstants;
  public loading: boolean;
  public factorsUsedInFormula: Array<RiskFactor> = [];

  constructor(private dialogService: DialogService,
              private agilityRiskService: AgilityRiskService,
              private translateService: TranslateService,
              private activatedRoute: ActivatedRoute,
              private changeDetectorRef: ChangeDetectorRef,
              private router: Router,
              private messageService: AgilityRiskMessageService,
              private formulaHelper: AgilityRiskFormulaHelperService,
              private navigationService: AgilityRiskNavigationService) {
    this.distributionId = this.activatedRoute.snapshot?.params?.id;
    this.surveyUserId = this.agilityRiskService.getSurveyUserId();
  }

  ngOnInit(): void {
    this.getAssessmentDistribution();
    this.getCustomFields();
  }

  openDefinitionsModal() {
    this.dialogService.open(RiskAssessmentSurveyDefinitionsModalComponent, {
      data: {
        riskFactors: this.assessment.factors
      },
      header: this.translateService.instant('common.definitions'),
      width: '50%'
    });
  }

  openSurveyDetailsModal() {
    this.dialogService.open(RiskAssessmentSurveyDetailsModalComponent, {
      data: {
        assessment: this.assessment
      },
      header: this.translateService.instant('assessment.survey.surveyDetails'),
      width: '70%'
    });
  }

  toggleExpandAllThreats() {
    this.expandAllRisks = !this.expandAllRisks;

    if (this.expandAllRisks) {
      // expand all threat accordion
      this.activeThreatIndexes = Array.from({length: this.assessmentDistributionRisks.length}, (v, i: number) => i);
    } else {
      this.activeThreatIndexes = '';
    }

    this.changeDetectorRef.detectChanges();
  }

  getAssessmentDistribution() {
    this.agilityRiskService.getAssessmentDistribution(this.distributionId)
      .subscribe((assessmentDistribution: AssessmentDistribution) => {
        this.assessmentDistribution = assessmentDistribution;
        this.assessment = this.assessmentDistribution.riskAssessment;
        this.getAssessmentRisks();
        this.getRiskAssessmentComments();
        this.riskFormulaString = this.formulaHelper.getFormulaAsString(this.assessment.riskFormula);
        const formulaAsArray = this.formulaHelper.getRiskFormulaAsArray(this.assessment.riskFormula);

        // keep only the factors that are actually used in the
        this.factorsUsedInFormula = this.formulaHelper.getFactorsUsedInFormula(formulaAsArray, this.assessment.factors);

        // build the overall risk dropdown
        this.overallRiskOptions = Array(this.assessment?.riskBands[this.assessment?.riskBands?.length - 1]?.numericValue).fill(null).map((_, i: number) => {
          return {
            label: i + 1,
            value: i + 1
          };
        });
      });
  }

  getAssessmentRisks() {
    this.loading = true;
    this.agilityRiskService.getAssessmentRisks(this.assessment.id)
      .subscribe(
        (assessmentDistributionRisks: Array<AssessmentDistributionRisk>) => {
          this.assessmentDistributionRisks = assessmentDistributionRisks;

          // set the comment string for current user
          this.comment = this.assessment.comments.find((comment: AssessmentComment) => comment.contact.id === this.surveyUserId)?.comments || '';

          this.assessmentDistributionRisks.forEach((adr: AssessmentDistributionRisk) => {
            const surveyUserMemberData: AssessmentMemberData = adr.riskAssessmentMembersData.find((amd: AssessmentMemberData) => amd.memberData?.member.id === this.surveyUserId);

            if (!surveyUserMemberData) {
              // create a AssessmentMemberData for survey's user if none exist
              // create a clone of the template to use to fill in the member object
              const theTemplateClone: AssessmentMemberData = _.cloneDeep(adr.riskAssessmentMembersData.find((amd: AssessmentMemberData) => amd.template));
              theTemplateClone.template = false;
              theTemplateClone.memberData = new AssessmentDistributionMemberData();
              theTemplateClone.memberData.member = {firstName: '', id: this.surveyUserId, isNotifiable: false, lastName: '', middleInitials: ''};

              // clear out the answer ids and set parent id
              theTemplateClone.memberAnswers.forEach((ma: AssessmentMemberAnswer) => {
                ma.parent = new BaseEntity();
                ma.parent.id = ma.id;
                ma.id = null;

                // filter out the factors that aren't being used in formula
                ma.factors = ma.factors.filter((f: AssessmentFactorSelection) => {
                  // NOTE: using == because for some reason factorsUsedInFormula.id is a string
                  return !!this.factorsUsedInFormula.find((rf: RiskFactor) => f.type.id == rf.id);
                });
              });

              adr.riskAssessmentMembersData.push(theTemplateClone);
            } else {
              // survey user data member exists - meaning the user has visited and answered & saved something before
              surveyUserMemberData?.memberAnswers.forEach((ma: AssessmentMemberAnswer) => {
                // filter out the factors that aren't being used in formula
                ma.factors = ma.factors.filter((f: AssessmentFactorSelection) => {
                  // NOTE: using == because for some reason factorsUsedInFormula.id is a string
                  return !!this.factorsUsedInFormula.find((rf: RiskFactor) => f.type.id == rf.id);
                });

                ma.factors?.forEach((assessmentFactorSelection: AssessmentFactorSelection) => {
                  if (assessmentFactorSelection?.factor && assessmentFactorSelection?.factor?.id) {
                    // NOTE!!! using numericValue for data binding
                    assessmentFactorSelection.factor.numericValue = assessmentFactorSelection.type.ranks.find((rfr: RiskFactorRank) => rfr.id === assessmentFactorSelection.factor.id)?.numericValue || 0;
                  }
                });
              });
            }
          });

          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
  }

  getCurrentUserAssessmentMemberAnswerByTemplate(assessmentDistributionRisks: AssessmentDistributionRisk, templateAssessmentMemberAnswer: AssessmentMemberAnswer): AssessmentMemberAnswer {
    // get the answer for the current user
    // if none, create a new blank one - altho this should never be the case. a blank member answer should have been created at init time.
    return assessmentDistributionRisks?.riskAssessmentMembersData
      ?.find((assessmentMemberData: AssessmentMemberData) => {
        return !assessmentMemberData.template && assessmentMemberData?.memberData?.member.id === this.surveyUserId;
      })
      ?.memberAnswers?.find((ama: AssessmentMemberAnswer) => {
        return ama.parent?.id === templateAssessmentMemberAnswer.id;
      }) || new AssessmentMemberAnswer();
  }

  openMemberAnswersModal(templateAssessmentMemberAnswer: AssessmentMemberAnswer, assessmentMembersData: Array<AssessmentMemberData>) {
    this.dialogService.open(RiskAssessmentMemberAnswersModalComponent, {
      data: {
        templateAssessmentMemberAnswer,
        riskAssessmentMembersData: assessmentMembersData,
        surveyUserId: this.surveyUserId
      },
      header: this.translateService.instant('assessment.survey.memberAnswers'),
      width: '50%'
    });
  }

  riskIsComplete(risk: AssessmentDistributionRisk): boolean {
    // find any answers that surveyUserId haven't answered
    return !risk.riskAssessmentMembersData.find((ramd: AssessmentMemberData) => {
      return ramd.memberData?.member?.id === this.surveyUserId && !!(!ramd.template && ramd.memberAnswers.find((ma: AssessmentMemberAnswer) => {
        return (ma.type === this.questionType.yesNo && (!ma.questionAnswer || ma.questionAnswer?.length === 0) || // this checks the normal yes/no questions for answers
          (ma.type === this.questionType.impactProbability && (ma.factors.find((f: AssessmentFactorSelection) => !f?.factor?.numericValue || f?.factor?.numericValue < 0 || !f?.factor?.id)))); // this checks the impactProbability sliders to make sure all has something;
      }));
    });
  }

  postAssessmentDistributionRisk(): void {
    // deep clone so can rip it apart w/o modifying original before send to server to save
    const assessmentDistributionRisksClone: AssessmentDistributionRisk[] = _.cloneDeep(this.assessmentDistributionRisks);

    assessmentDistributionRisksClone.forEach((adr: AssessmentDistributionRisk) => {
      // remove members that's not current users
      adr.riskAssessmentMembersData = adr.riskAssessmentMembersData.filter((amd: AssessmentMemberData) => amd.memberData?.member.id === this.surveyUserId);

      // remove customValues that is formula type
      adr.riskAssessmentMembersData.forEach((amd: AssessmentMemberData) => {
        amd.memberCustomValues = amd.memberCustomValues.filter((cv: CustomValue<any>) => cv.type !== this.customFieldConstants.formula);
      });
    });

    this.saving = true;

    this.agilityRiskService.postAssessmentDistributionRisk(this.distributionId, assessmentDistributionRisksClone, this.comment)
      .subscribe(
        res => {
          this.messageService.success(this.translateService.instant('common.saved'));
        },
        err => {
          // todo: maybe something for preparis.
          this.saving = false;
        },
        () => {
          this.saving = false;
          const loggedUser = this.agilityRiskService.getLoggedInUser();
          if (loggedUser && loggedUser.role !== 'LISTED') {
            this.router.navigate(['summary', this.distributionId], {relativeTo: this.activatedRoute.parent});
          } else {
            this.router.navigateByUrl(this.navigationService.moduleBaseAddress + '/assessment/complete');
          }
        }
      );
  }

  getRiskAssessmentMemberAnswersTemplate(assessmentDistributionRisk: AssessmentDistributionRisk): Array<AssessmentMemberAnswer> {
    return assessmentDistributionRisk.riskAssessmentMembersData.find((amd: AssessmentMemberData) => amd.template)?.memberAnswers;
  }

  getCurrentUserAssessmentMemberData(assessmentDistributionRisk: AssessmentDistributionRisk): AssessmentMemberData {
    return assessmentDistributionRisk.riskAssessmentMembersData.find((amd: AssessmentMemberData) => !amd.template && amd.memberData?.member?.id === this.surveyUserId);
  }

  getCustomFields(): void {
    this.agilityRiskService.getCustomFields('RISK')
      .subscribe((customFields: Array<CustomField>) => {
        this.customFields = customFields;
      });
  }

  openCustomFieldsModal(assessmentDistributionRisk: AssessmentDistributionRisk) {
    const currentUserAssessmentMemberData: AssessmentMemberData = this.getCurrentUserAssessmentMemberData(assessmentDistributionRisk);

    if (!currentUserAssessmentMemberData?.memberCustomValues?.length) {
      // build a new array of custom fields to store user's selections
      currentUserAssessmentMemberData.memberCustomValues = new Array<CustomValue<any>>();

      this.customFields.forEach((customField: CustomField) => {
        currentUserAssessmentMemberData.memberCustomValues.push({
          field: {
            id: customField.id.toString().toLowerCase(),
            name: customField.name
          },
          id: null,
          value: null,
          type: customField.type.toLowerCase() // server asks for lowerCase...
        });
      });
    }

    const ref = this.dialogService.open(RiskAssessmentSurveyCustomFieldsModalComponent, {
      data: {
        customFields: this.customFields,
        memberCustomValues: currentUserAssessmentMemberData?.memberCustomValues,
      },
      header: this.translateService.instant('assessment.survey.customFields'),
      width: '50%'
    });

    ref.onClose.subscribe((res: Array<CustomValue<any>>) => {
      if (res) {
        currentUserAssessmentMemberData.memberCustomValues = res;
      }
    });
  }

  getRiskAssessmentComments() {
    this.agilityRiskService.getRiskAssessmentComments(this.assessment.id)
      .subscribe((comments: Array<AssessmentComment>) => {
        this.comment = comments.find((comment: AssessmentComment) => comment.contact.id === this.surveyUserId)?.comments || '';
      });
  }

  additionalCommentIsRequired(): boolean {
    return !!(this.assessmentDistributionRisks?.find((adr: AssessmentDistributionRisk) => adr.override) && !this.comment?.length);
  }
}
